/* eslint-disable react/react-in-jsx-scope */
import * as pdfjsLib from "pdfjs-dist";
import { useState } from "react";

import IconImg from "@/assets/icones/images.svg?react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";
import { copyDocumentLink } from "@/documents/utils/copyDocumentLink";
import { downloadDocumentFile } from "@/documents/utils/downloadDocumentFile";
import {
  showDownloadErrorToast,
  showDownloadInProgressToast,
  showDownloadSuccessToast,
} from "@/documents/utils/downloadToast";
import pdfWorker from "@/pdf-worker";
import { Download, Eye, Info, Link, MoreVertical, Trash2 } from "lucide-react";
import DocumentDetailsDialog from "./DocumentDetailsDialog";
pdfjsLib.GlobalWorkerOptions.workerPort = new pdfWorker();

type ImageThumbnailProps = {
  url: string;
  downloadUrl?: string;
  treeFinancer: any;
  pj: any;
  otherpage?: boolean;
  canDelete?: boolean;
  onDelete?: (id: number) => void | Promise<void>;
  onShowDetails?: (doc: any) => void;
};

export const ImageThumbnail = ({
  url,
  downloadUrl,
  treeFinancer,
  pj,
  otherpage = false,
  canDelete = false,
  onDelete,
  onShowDetails,
}: ImageThumbnailProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const handleCopyLink = async () => {
    try {
      await copyDocumentLink(url);
      toast({ title: "Lien copié" });
    } catch {
      toast({ title: "Impossible de copier le lien", variant: "destructive" });
    }
  };
  const handleDownload = async () => {
    const downloadToast = showDownloadInProgressToast({
      filename: pj.name,
      kind: "file",
    });

    try {
      await downloadDocumentFile(downloadUrl ?? url, pj.name);
      downloadToast.dismiss();
      showDownloadSuccessToast({ filename: pj.name, kind: "file" });
    } catch {
      downloadToast.dismiss();
      showDownloadErrorToast({ filename: pj.name, kind: "file" });
    }
  };
  const handleShowDetails = () => {
    if (onShowDetails) {
      onShowDetails(pj);
      return;
    }

    setDetailsOpen(true);
  };
  return (
    <div className={`bg-muted  ${!otherpage && "p-3"} rounded-md relative`}>
      <div className="flex justify-between items-center relative z-10 mb-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="flex items-center gap-3 "
                style={{ width: "calc(100% - 45px)" }}
              >
                <IconImg width={18} height={18} />
                <h3 className="truncate flex-1">{pj.name}</h3>
              </div>
            </TooltipTrigger>
            <TooltipContent className="flex flex-col gap-2">
              <div>
                Porteur de projet :{" "}
                <strong>
                  {treeFinancer?.find((t) => t.id === pj.pp)?.name}
                </strong>
              </div>
              <div>
                Programme :{" "}
                <strong>
                  {
                    treeFinancer
                      ?.find((t) => t.id === pj.pp)
                      ?.programs.find((p) => p.id === pj.program)?.name
                  }
                </strong>
              </div>
              <div>
                Projet :{" "}
                <strong>
                  {
                    treeFinancer
                      ?.find((t) => t.id === pj.pp)
                      ?.programs.find((p) => p.id === pj.program)
                      ?.projects.find((p) => p.id === pj.project)?.name
                  }
                </strong>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {!otherpage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="px-2 outline-none border-0 border-none ring-0 hover:bg-black/5 w-auto h-auto"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <MoreVertical className="text-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="flex flex-col gap-2" align="end">
              <DropdownMenuItem asChild>
                <button
                  onClick={() => setModalOpen(true)}
                  className="flex gap-2 items-center cursor-pointer text-foreground"
                >
                  <Eye width={22} height={22} /> Ouvrir
                </button>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleShowDetails();
                  }}
                  className="flex gap-2 items-center cursor-pointer text-foreground"
                >
                  <Info width={22} height={22} /> Voir les détails
                </button>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDownload();
                  }}
                  className="flex gap-2 items-center cursor-pointer text-foreground"
                >
                  <Download width={22} height={22} /> Télécharger
                </button>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCopyLink();
                  }}
                  className="flex gap-2 items-center cursor-pointer text-foreground"
                >
                  <Link width={22} height={22} /> Copier le lien
                </button>
              </DropdownMenuItem>
              {canDelete && (
                <DropdownMenuItem asChild>
                  <button
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      void onDelete?.(Number(pj.id));
                    }}
                    className="flex gap-2 items-center cursor-pointer text-red-500"
                  >
                    <Trash2 width={22} height={22} /> Supprimer
                  </button>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogTrigger asChild>
          <div className="relative h-[17rem] overflow-hidden rounded-md bg-white flex items-center justify-center cursor-pointer">
            <img
              src={url}
              alt={pj.name}
              className="object-cover w-full h-full"
            />
          </div>
        </DialogTrigger>

        <DialogContent className="sm:max-w-4xl sm:w-full h-[80vh] flex flex-col z-[100000]">
          <img
            src={url}
            alt={pj.name}
            className="w-auto h-[90%] object-contain"
          />

          <DialogClose asChild>
            <Button variant="outline" className="mt-4 w-max m-auto">
              Fermer
            </Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
      <DocumentDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        pj={pj}
        treeFinancer={treeFinancer}
      />
    </div>
  );
};

export default ImageThumbnail;
